import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormArray, FormControl, AbstractControl } from '@angular/forms';
import { FreightForwarderBidService, CreateFreightForwarderBidRequest } from '../../../services/api/freight-forwarder-bid';
import { DictionaryService } from '../../../services/api/dictionary';
import { CargoRequestService, CargoRequest } from '../../../services/api/cargo-request';
import { LanguageService } from '../../../services/i18n/language';
import { AuthService } from '../../../services/auth/auth';
import { Subscription } from 'rxjs';
import { formatRequestNumber } from '../../../services/request-number';

@Component({
  selector: 'app-create-bid',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './create-bid.html',
  styleUrl: './create-bid.scss'
})
export class CreateBidComponent implements OnInit, OnDestroy {
  bidForm: FormGroup;
  cargoRequest: CargoRequest | null = null;
  currencies: any[] = [];
  services: any[] = [];
  loading = false;
  error: string | null = null;
  isEditMode = false;
  bidId: number | null = null;
  returnTo: string | null = null; // Откуда пришли: 'dashboard' или 'requests'
  /** Услуги ставки при редактировании — запоминаем, т.к. справочник услуг может прийти позже самой ставки. */
  private selectedServiceIds = new Set<number>();

  // Translations
  titleText = 'Сделать ставку';
  editTitleText = 'Редактировать ставку';
  priceLabel = 'Цена';
  currencyLabel = 'Валюта';
  deliveryDaysLabel = 'Срок доставки (дней)';
  servicesLabel = 'Услуги';
  noteLabel = 'Примечание';
  selectCurrencyText = 'Выберите валюту';
  submitText = 'Создать ставку';
  updateText = 'Обновить ставку';
  submittingText = 'Создание...';
  updatingText = 'Обновление...';
  cancelText = 'Отмена';
  requiredText = '*';
  daysText = 'дней';
  loadingText = 'Загрузка...';
  cargoRequestInfoText = 'Информация о запросе';
  routeText = 'Маршрут';
  loadingDateText = 'Дата погрузки';
  totalWeightText = 'Общий вес';
  totalVolumeText = 'Общий объем';
  
  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private bidService: FreightForwarderBidService,
    private dictionaryService: DictionaryService,
    private cargoRequestService: CargoRequestService,
    private languageService: LanguageService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {
    this.bidForm = this.fb.group({
      price: ['', [Validators.required, Validators.min(0.01)]],
      currencyId: [null, [Validators.required]],
      deliveryDays: ['', [Validators.required, Validators.min(1), Validators.max(365)]],
      serviceIds: this.fb.array([], [Validators.required, this.atLeastOneSelected]),
      note: ['']
    });
  }

  ngOnInit(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('CreateBid: Running on server (SSR), skipping initialization');
      return;
    }

    setTimeout(() => {
      if (!this.authService.isAuthenticated()) {
        console.warn('CreateBid: User not authenticated, redirecting to login');
        this.router.navigate(['/login']);
        return;
      }

      const user = this.authService.getUser();
      if (user?.role?.code !== 'ROLE_FREIGHT_FORWARDER') {
        console.warn('CreateBid: User is not freight forwarder, redirecting to home');
        this.router.navigate(['/']);
        return;
      }

      // Получаем параметр returnTo из query string
      this.returnTo = this.route.snapshot.queryParams['returnTo'] || null;
      
      this.route.params.subscribe(params => {
        const cargoRequestId = params['cargoRequestId'];
        const id = params['id'];
        
        if (id) {
          this.bidId = +id;
          this.isEditMode = true;
          this.loadBid(+id);
        } else if (cargoRequestId) {
          this.loadCargoRequest(+cargoRequestId);
        }
      });

      this.loadDictionaries();
      this.updateTranslations();
      
      this.subscriptions.add(
        this.languageService.getTranslations().subscribe(() => {
          this.updateTranslations();
        })
      );
    }, 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateTranslations(): void {
    this.titleText = this.languageService.translate('freightForwarder.bid.title') || 'Сделать ставку';
    this.editTitleText = this.languageService.translate('freightForwarder.bid.editTitle') || 'Редактировать ставку';
    this.priceLabel = this.languageService.translate('freightForwarder.bid.price') || 'Цена';
    this.currencyLabel = this.languageService.translate('freightForwarder.bid.currency') || 'Валюта';
    this.deliveryDaysLabel = this.languageService.translate('freightForwarder.bid.deliveryDays') || 'Срок доставки (дней)';
    this.servicesLabel = this.languageService.translate('freightForwarder.bid.services') || 'Услуги';
    this.noteLabel = this.languageService.translate('freightForwarder.bid.note') || 'Примечание';
    this.selectCurrencyText = this.languageService.translate('freightForwarder.bid.selectCurrency') || 'Выберите валюту';
    this.submitText = this.languageService.translate('freightForwarder.bid.submit') || 'Создать ставку';
    this.updateText = this.languageService.translate('freightForwarder.bid.update') || 'Обновить ставку';
    this.submittingText = this.languageService.translate('freightForwarder.bid.submitting') || 'Создание...';
    this.updatingText = this.languageService.translate('freightForwarder.bid.updating') || 'Обновление...';
    this.cancelText = this.languageService.translate('freightForwarder.bid.cancel') || 'Отмена';
    this.requiredText = this.languageService.translate('freightForwarder.bid.required') || '*';
    this.daysText = this.languageService.translate('freightForwarder.bid.days') || 'дней';
    this.loadingText = this.languageService.translate('freightForwarder.bid.loading') || 'Загрузка...';
    this.cargoRequestInfoText = this.languageService.translate('freightForwarder.bid.cargoRequestInfo') || 'Информация о запросе';
    this.routeText = this.languageService.translate('freightForwarder.bid.route') || 'Маршрут';
    this.loadingDateText = this.languageService.translate('freightForwarder.bid.loadingDate') || 'Дата погрузки';
    this.totalWeightText = this.languageService.translate('freightForwarder.bid.totalWeight') || 'Общий вес';
    this.totalVolumeText = this.languageService.translate('freightForwarder.bid.totalVolume') || 'Общий объем';
    this.cdr.markForCheck();
  }

  loadDictionaries(): void {
    this.subscriptions.add(
      this.dictionaryService.getCurrencies().subscribe({
        next: (currencies) => {
          this.currencies = currencies;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading currencies', err);
        }
      })
    );

    this.subscriptions.add(
      this.dictionaryService.getAllFreightForwarderServices().subscribe({
        next: (services) => {
          this.services = this.sortServices(services);
          this.initializeServiceCheckboxes();
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading services', err);
        }
      })
    );
  }

  /** Порядок услуг — как идёт перевозка: забор → экспорт → транзит → доставка → растаможка → СВХ → контроль. Остальные — в конце. */
  private static readonly SERVICE_ORDER = [
    'Авто забор',
    'Оформление экспортной декларации',
    'Транзитное оформление',
    'Транспортировка до пункта назначения',
    'Таможенная очистка грузов',
    'Услуги СВХ',
    'Ежедневное отслеживание груза',
    'Фотоотчет',
  ];

  private sortServices(services: any[]): any[] {
    const norm = (v: string | undefined) => (v || '').trim().toLowerCase().replace(/ё/g, 'е');
    const order = CreateBidComponent.SERVICE_ORDER.map(norm);
    const rank = (s: any) => {
      const i = order.indexOf(norm(s.nameRu));
      return i === -1 ? order.length : i;
    };
    return [...services].sort((a, b) => rank(a) - rank(b));
  }

  initializeServiceCheckboxes(): void {
    const serviceFormArray = this.bidForm.get('serviceIds') as FormArray;
    serviceFormArray.clear();
    
    this.services.forEach((service) => {
      serviceFormArray.push(new FormControl(this.selectedServiceIds.has(service.id)));
    });
  }

  loadCargoRequest(id: number): void {
    this.loading = true;
    this.subscriptions.add(
      this.cargoRequestService.getCargoRequest(id).subscribe({
        next: (request) => {
          this.cargoRequest = request;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading cargo request', err);
          this.error = 'Ошибка при загрузке запроса';
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  loadBid(id: number): void {
    this.loading = true;
    this.subscriptions.add(
      this.bidService.getBid(id).subscribe({
        next: (bid) => {
          // Проверяем, не принята ли ставка или не проиграла ли
          if (bid.bidStatus === 'ACCEPTED' || bid.bidStatus === 'LOST') {
            console.warn('CreateBid: Bid is accepted or lost, editing not allowed');
            const statusText = bid.bidStatus === 'ACCEPTED' 
              ? 'принятую' 
              : 'проигравшую';
            this.error = `Невозможно редактировать ${statusText} ставку`;
            this.loading = false;
            setTimeout(() => {
              this.router.navigate(['/freight-forwarder/dashboard/bids']);
            }, 2000);
            return;
          }
          
          this.populateForm(bid);
          if (bid.cargoRequestId) {
            this.loadCargoRequest(bid.cargoRequestId);
          }
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading bid', err);
          this.error = 'Ошибка при загрузке ставки';
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  populateForm(bid: any): void {
    this.bidForm.patchValue({
      price: bid.price,
      currencyId: bid.currencyId,
      deliveryDays: bid.deliveryDays,
      note: bid.note || ''
    });

    this.selectedServiceIds = new Set((bid.services ?? []).map((s: any) => s.id));
    if (this.services.length) this.initializeServiceCheckboxes();
  }

  get serviceIdsFormArray(): FormArray {
    return this.bidForm.get('serviceIds') as FormArray;
  }

  getServiceControl(index: number): FormControl {
    return this.serviceIdsFormArray.at(index) as FormControl;
  }

  isServiceSelected(index: number): boolean {
    return this.serviceIdsFormArray.at(index).value;
  }

  atLeastOneSelected = (control: AbstractControl) => {
    if (!(control instanceof FormArray)) {
      return null;
    }
    const atLeastOne = control.controls.some(c => c.value === true);
    return atLeastOne ? null : { atLeastOneRequired: true };
  };

  onSubmit(): void {
    if (this.bidForm.invalid) {
      this.markFormGroupTouched(this.bidForm);
      return;
    }

    this.loading = true;
    this.error = null;

    const formValue = this.bidForm.value;
    const selectedServiceIds = this.services
      .map((service, index) => formValue.serviceIds[index] ? service.id : null)
      .filter(id => id !== null);

    const request: CreateFreightForwarderBidRequest = {
      cargoRequestId: this.cargoRequest?.id || 0,
      price: parseFloat(formValue.price),
      currencyId: formValue.currencyId,
      deliveryDays: parseInt(formValue.deliveryDays),
      serviceIds: selectedServiceIds as number[],
      note: formValue.note || undefined
    };

    const operation = this.isEditMode && this.bidId
      ? this.bidService.updateBid(this.bidId, request)
      : this.bidService.createBid(request);

    this.subscriptions.add(
      operation.subscribe({
        next: (bid) => {
          this.loading = false;
          // После создания/редактирования возвращаемся на страницу запроса
          // Сохраняем параметр returnTo, чтобы кнопка "Назад" работала правильно
          const requestId = this.cargoRequest?.id || bid?.cargoRequestId;
          if (requestId) {
            const queryParams = this.returnTo ? { returnTo: this.returnTo } : {};
            this.router.navigate(['/freight-forwarder/request', requestId], { queryParams });
          } else {
            // Если нет cargoRequestId, возвращаемся на список запросов
            this.router.navigate(['/freight-forwarder/requests']);
          }
        },
        error: (err) => {
          this.loading = false;
          if (err.error && err.error.message) {
            this.error = err.error.message;
          } else if (err.error && typeof err.error === 'string') {
            this.error = err.error;
          } else {
            this.error = this.languageService.translate('freightForwarder.bid.errors.default') || 'Ошибка при создании ставки. Попробуйте еще раз.';
          }
          this.cdr.markForCheck();
        }
      })
    );
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormArray) {
        control.controls.forEach(c => c.markAsTouched());
      }
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getFieldError(fieldName: string): string {
    const field = this.bidForm.get(fieldName);
    if (field && field.touched && field.errors) {
      if (field.errors['required']) {
        return this.languageService.translate('freightForwarder.bid.errors.required') || 'Это поле обязательно для заполнения';
      }
      if (field.errors['min']) {
        return this.languageService.translate('freightForwarder.bid.errors.min') || `Минимальное значение: ${field.errors['min'].min}`;
      }
      if (field.errors['max']) {
        return this.languageService.translate('freightForwarder.bid.errors.max') || `Максимальное значение: ${field.errors['max'].max}`;
      }
    }
    
    if (fieldName === 'serviceIds') {
      const serviceArray = this.bidForm.get('serviceIds') as FormArray;
      if (serviceArray.errors && serviceArray.errors['atLeastOneRequired']) {
        return this.languageService.translate('freightForwarder.bid.errors.atLeastOneService') || 'Необходимо выбрать хотя бы одну услугу';
      }
    }
    
    return '';
  }

  getRoute(): string {
    if (!this.cargoRequest) return '';
    return `${this.cargoRequest.fromCity} → ${this.cargoRequest.toCity}`;
  }

  getTotalWeight(): number {
    if (!this.cargoRequest) return 0;
    return this.cargoRequest.products.reduce((sum, product) => sum + product.totalWeightKg, 0);
  }

  getTotalVolume(): number {
    if (!this.cargoRequest) return 0;
    return this.cargoRequest.products.reduce((sum, product) => {
      // Если есть volumeM3, используем его
      if (product.volumeM3) {
        return sum + product.volumeM3;
      }
      // Иначе вычисляем из габаритов одного места × количество мест
      if (product.lengthMm && product.widthMm && product.heightMm) {
        const volume = (product.lengthMm * product.widthMm * product.heightMm) / 1_000_000_000 * (product.quantity || 1);
        return sum + volume;
      }
      // Если ни габариты, ни объем не заполнены, возвращаем 0
      return sum;
    }, 0);
  }

  formatWeight(weight: number): string {
    if (!weight) return '—';
    return `${new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: 1 }).format(weight)} ${this.t('ffSearch.kg', 'кг')}`;
  }

  formatVolume(volume: number): string {
    if (!volume) return '—';
    return new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: volume < 1 ? 3 : 1 }).format(volume) + ' м³';
  }

  getServiceName(service: any): string {
    const currentLang = this.languageService.getCurrentLanguageValue();
    return currentLang === 'en' ? service.nameEn : service.nameRu;
  }

  onCancel(): void {
    const requestId = this.cargoRequest?.id;
    if (requestId) {
      const queryParams = this.returnTo ? { returnTo: this.returnTo } : {};
      this.router.navigate(['/freight-forwarder/request', requestId], { queryParams });
    } else {
      this.router.navigate(['/freight-forwarder/requests']);
    }
  }

  // ─── Отображение ──────────────────────────────────────────────────────────

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  private get numberLocale(): string {
    const lang = this.languageService.getCurrentLanguageValue();
    return ({ ru: 'ru-RU', kk: 'kk-KZ', en: 'en-US', zh: 'zh-CN' } as Record<string, string>)[lang] ?? 'ru-RU';
  }

  get requestNo(): string {
    return this.cargoRequest ? formatRequestNumber(this.cargoRequest.id, this.cargoRequest.createdAt) : '';
  }

  rateLabel(rateType: string | null | undefined): string {
    switch (rateType) {
      case 'FTL': return this.t('ffSearch.rateFtl', 'Фура (FTL)');
      case 'LTL': return this.t('ffSearch.rateLtl', 'Сборный (LTL)');
      case 'AIR': return this.t('ffSearch.rateAir', 'Авиа');
      case 'CONTAINER': return this.t('ffSearch.rateContainer', 'Контейнер');
      default: return rateType || '';
    }
  }

  /** Поле с ошибкой — подсвечиваем после касания или попытки отправить. */
  invalid(name: string): boolean {
    const c = this.bidForm.get(name);
    return !!c && c.invalid && c.touched;
  }

  get servicesInvalid(): boolean {
    const arr = this.serviceIdsFormArray;
    return arr.invalid && (arr.touched || arr.controls.some(c => c.touched));
  }

  get placesCount(): number {
    return (this.cargoRequest?.products ?? []).reduce((sum, p) => sum + (p.quantity || 1), 0);
  }

  currencyCode(currencyId: number | undefined): string {
    return this.currencies.find(c => c.id === currencyId)?.code || '';
  }

  formatMoney(amount: number | undefined, code: string): string {
    return `${new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: 0 }).format(Math.round(amount || 0))} ${code}`.trim();
  }

  /** Особенности груза: флаг хотя бы у одной позиции — показываем его, иначе — обратное. */
  get cargoTraits(): { label: string; warn: boolean }[] {
    const products = this.cargoRequest?.products ?? [];
    const any = (key: 'isDangerous' | 'isOversized' | 'isNonStackable') => products.some(p => !!p[key]);
    return [
      any('isDangerous') ? { label: this.t('ffSearch.dangerous', 'Опасный груз'), warn: true } : { label: this.t('ffRequest.notDangerous', 'Неопасный груз'), warn: false },
      any('isOversized') ? { label: this.t('ffSearch.oversized', 'Крупногабаритный груз'), warn: true } : { label: this.t('ffRequest.notOversized', 'Стандартные габариты'), warn: false },
      any('isNonStackable') ? { label: this.t('ffSearch.nonStackable', 'Не штабелировать'), warn: false } : { label: this.t('ffRequest.stackable', 'Можно штабелировать'), warn: false },
    ];
  }
}
